import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FaSpinner, FaCheck, FaTrash } from "react-icons/fa";
import { AiOutlineUpload } from "react-icons/ai";
import _ from "lodash";
import { toast } from "react-toastify";

import { gateway } from "../../../../../../utils/api";
import { toastDefaultOptions } from "../../../../../../utils/functions";
import { Container, DropZone, LoadingWrapper, FileList } from "./styles";

const MAX_FILE_SIZE_BYTES = 200 * 1024 * 1024;
const MAX_FILES = 5;
const FILE_SIZE_ERROR_MESSAGE = "O arquivo é maior que 200MB. Envie um arquivo menor ou compartilhe um link na nuvem, por favor.";
const FILE_COUNT_ERROR_MESSAGE = "Você pode enviar no máximo 5 arquivos.";

function Upload({ value, index, onAnswerChange, id, isEdting }) {

  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]); // Lista dos arquivos enviados

  const onDrop = useCallback(async (acceptedFiles, fileRejections = []) => {
    if (uploadedFiles.length >= MAX_FILES) {
      toast.error(FILE_COUNT_ERROR_MESSAGE, {
        ...toastDefaultOptions
      });
      return;
    }

    const hasTooManyFilesRejection = fileRejections.some((fileRejection) =>
      (fileRejection.errors || []).some((error) => error.code === "too-many-files")
    );

    if (hasTooManyFilesRejection) {
      toast.error(FILE_COUNT_ERROR_MESSAGE, {
        ...toastDefaultOptions
      });
    }

    if (acceptedFiles.length > 0 && uploadedFiles.length < MAX_FILES) {
      const filesWithinLimit = acceptedFiles.filter((file) => file.size <= MAX_FILE_SIZE_BYTES);

      if (filesWithinLimit.length !== acceptedFiles.length) {
        toast.error(FILE_SIZE_ERROR_MESSAGE, {
          ...toastDefaultOptions
        });
      }

      if (filesWithinLimit.length === 0) {
        return;
      }

      try {
        setLoading(true);
  
        const uploadPromises = filesWithinLimit.map(async (file) => {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("mediaType", "ASSESSMENT_RESPONSE");
  
          const response = await gateway.post("/media/media/upload", formData);
          const fileCode = _.get(response, "ox_standard.ox_identification.cr_code", null);
  
          return fileCode ? { name: file.name, code: fileCode } : null;
        });
  
        const newFiles = await Promise.all(uploadPromises);
        const validFiles = newFiles.filter(file => file !== null); // Removing invalide files
  
        const filesForUpdate = [...uploadedFiles, ...validFiles].slice(0, MAX_FILES); // Limit by 5 files

        const removed = await onAnswerChange(id, filesForUpdate.map((file) => file.code));
        if(removed){
          setUploadedFiles(filesForUpdate);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    } else {
      toast.error(FILE_COUNT_ERROR_MESSAGE, {
        ...toastDefaultOptions
      });
    }
  }, [uploadedFiles, index, onAnswerChange]);
  
  const removeFile = async (fileCode) => {
    try {
      const updatedFilesRemoved = (uploadedFiles || []).filter((file) => file.code !== fileCode);
      const removed = await onAnswerChange(id, updatedFilesRemoved.map((file) => file.code));
      if(removed){
        setUploadedFiles(updatedFilesRemoved);
      }
    } catch (error) {
      // 
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    disabled: loading,
    multiple: true,
    maxFiles: MAX_FILES - uploadedFiles.length
  });

  useEffect(() => {
    if(Array.isArray(value)){
      const alreadyFilesArr = value.map(item => {
        return {
          name: item.name,
          code: item.code
        }
      })
      setUploadedFiles(alreadyFilesArr);
    } else {
      setUploadedFiles([{
        name: value.name,
        code: value.code
      }])
    }
  }, []);

  return (
    <Container>
      {isEdting && (<DropZone {...getRootProps()}>
        <input {...getInputProps()} />

        {loading ? (
          <LoadingWrapper>
            <FaSpinner size={20} color="#1890ff" />
            <span>Realizando upload dos seus arquivos ...</span>
          </LoadingWrapper>
        ) : (
          <p id="text-upload">
            Arraste e solte até 5 arquivos aqui ou clique para selecionar.
            <AiOutlineUpload />
          </p>
        )}
      </DropZone>)}

      {uploadedFiles.length > 0 && (
        <FileList>
          {uploadedFiles.map((file) => (
            <div key={file.code} className="file-item">
              <FaCheck size={20} color="#1890ff" />
              <span>{file.name}</span>
              {isEdting && (<FaTrash onClick={() => removeFile(file.code)} />)}
            </div>
          ))}
        </FileList>
      )}
    </Container>
  );
}

export default Upload;
